import {
  OrderTransportError,
  type CartSnapshot,
  type CartSummary,
  type GatewayMutationResult,
  type MutationResolution,
  type OrderLineGateway,
  type OrderMutationConflict,
  type PvcOrderSubmission,
} from "./order-line-controller";
import type {
  PvcQuoteInput,
  QuoteGateway,
  QuoteGatewayResult,
} from "./pricing-controller";
import {
  ShopApiRequestError,
  type ShopApiGraphqlClient,
} from "./shop-api-transport";

export class AdapterContractError extends Error {
  constructor(message: string, public readonly details?: unknown) {
    super(message);
    this.name = "AdapterContractError";
  }
}

export interface GraphqlBinding<TInput, TWire> {
  operationName: string;
  document: string;
  variables(input: TInput): Record<string, unknown>;
  decode(data: unknown): TWire;
}

export type WireOrderLineResult = {
  orderLineId: string;
  orderLineVersion: number;
  cart: CartSnapshot;
};

export type WireGatewayMutationResult =
  | {
      type: "success";
      clientMutationId?: string | null;
      result: WireOrderLineResult;
    }
  | {
      type: "conflict";
      clientMutationId?: string | null;
      conflict: OrderMutationConflict;
    };

export type WireMutationResolution =
  | {
      status: "committed";
      clientMutationId?: string | null;
      result: WireOrderLineResult;
    }
  | { status: "pending"; clientMutationId?: string | null }
  | { status: "not-found"; clientMutationId?: string | null };

export type AddPvcInput = PvcOrderSubmission & { clientMutationId: string };
export type UpdatePvcInput = AddPvcInput & {
  orderLineId: string;
  expectedVersion: number;
};

export type PvcOrderBindings = {
  add: GraphqlBinding<AddPvcInput, WireGatewayMutationResult>;
  update: GraphqlBinding<UpdatePvcInput, WireGatewayMutationResult>;
  resolveMutation: GraphqlBinding<{ clientMutationId: string }, WireMutationResolution>;
  recoverCartSession: GraphqlBinding<Record<string, never>, { ok: true }>;
  resolveCartOwnership: GraphqlBinding<
    { choice: "guest-cart" | "account-cart" },
    { ok: true }
  >;
};

export type PvcQuoteBinding = GraphqlBinding<PvcQuoteInput, QuoteGatewayResult>;

function transportToOrderError(error: unknown): OrderTransportError {
  if (error instanceof OrderTransportError) return error;
  if (error instanceof ShopApiRequestError) {
    return new OrderTransportError(
      error.certainty,
      {
        code: error.code === "OFFLINE" ? "OFFLINE" : "NETWORK",
        cause: error,
      },
    );
  }
  // Any decoder/contract error happens after a response to a dispatched mutation.
  // The mutation may already have committed, so UNKNOWN is mandatory.
  return new OrderTransportError(
    "unknown",
    { code: "UNKNOWN", cause: error },
  );
}

function requireEcho(expected: string, actual: string | null | undefined, operation: string): string {
  if (actual !== expected) {
    throw new AdapterContractError(
      `${operation} did not echo the exact clientMutationId`,
      { expected, actual },
    );
  }
  return actual;
}

function normalizeMutation(
  expectedMutationId: string,
  operationName: string,
  wire: WireGatewayMutationResult,
): GatewayMutationResult {
  const clientMutationId = requireEcho(expectedMutationId, wire.clientMutationId, operationName);
  if (wire.type === "success") {
    return { type: "success", clientMutationId, result: wire.result };
  }
  return { type: "conflict", clientMutationId, conflict: wire.conflict };
}

function normalizeResolution(
  expectedMutationId: string,
  operationName: string,
  wire: WireMutationResolution,
): MutationResolution {
  const clientMutationId = requireEcho(expectedMutationId, wire.clientMutationId, operationName);
  if (wire.status === "committed") {
    return { status: "committed", clientMutationId, result: wire.result };
  }
  return { status: wire.status, clientMutationId };
}

async function executeBinding<TInput, TWire>(
  client: ShopApiGraphqlClient,
  binding: GraphqlBinding<TInput, TWire>,
  input: TInput,
  signal?: AbortSignal,
): Promise<TWire> {
  const data = await client.execute({
    operationName: binding.operationName,
    document: binding.document,
    variables: binding.variables(input),
    signal,
  });
  return binding.decode(data);
}

export function createPvcQuoteGateway(options: {
  client: ShopApiGraphqlClient;
  binding: PvcQuoteBinding;
}): QuoteGateway {
  return {
    async fetch(input, signal) {
      // configurationHash is NEVER created or transformed here. The decoder must
      // return the exact server value and the controller treats it as opaque.
      return executeBinding(options.client, options.binding, input, signal);
    },
  };
}

export function createPvcOrderLineGateway(options: {
  client: ShopApiGraphqlClient;
  bindings: PvcOrderBindings;
}): OrderLineGateway {
  const { client, bindings } = options;

  return {
    async addPvcSign(input) {
      try {
        const wire = await executeBinding(client, bindings.add, input);
        return normalizeMutation(input.clientMutationId, bindings.add.operationName, wire);
      } catch (error) {
        throw transportToOrderError(error);
      }
    },

    async updatePvcOrderLine(input) {
      try {
        const wire = await executeBinding(client, bindings.update, input);
        return normalizeMutation(input.clientMutationId, bindings.update.operationName, wire);
      } catch (error) {
        throw transportToOrderError(error);
      }
    },

    async resolveMutation(clientMutationId) {
      try {
        const wire = await executeBinding(client, bindings.resolveMutation, { clientMutationId });
        return normalizeResolution(clientMutationId, bindings.resolveMutation.operationName, wire);
      } catch (error) {
        throw transportToOrderError(error);
      }
    },

    async recoverCartSession() {
      try {
        const wire = await executeBinding(client, bindings.recoverCartSession, {});
        if (wire.ok !== true) throw new AdapterContractError("recoverCartSession did not return ok=true", wire);
      } catch (error) {
        throw transportToOrderError(error);
      }
    },

    async resolveCartOwnership(choice) {
      try {
        const wire = await executeBinding(client, bindings.resolveCartOwnership, { choice });
        if (wire.ok !== true) throw new AdapterContractError("resolveCartOwnership did not return ok=true", wire);
      } catch (error) {
        throw transportToOrderError(error);
      }
    },
  };
}

/**
 * Helper for live-adapter tests: asserts that an order submission contains the
 * exact opaque quote binding produced by the quote response. It does not hash,
 * sign or reinterpret either field.
 */
export function assertExactQuoteBinding(
  submission: PvcOrderSubmission,
  quote: { configurationHash: string; pricingVersion: string },
): void {
  if (
    submission.quote.configurationHash !== quote.configurationHash ||
    submission.quote.pricingVersion !== quote.pricingVersion
  ) {
    throw new AdapterContractError("Order submission does not carry the exact server quote binding", {
      submission: submission.quote,
      quote,
    });
  }
}

// Re-export these shapes so generated/introspection bindings can depend only on
// this adapter module rather than importing controller implementation details.
export type { CartSummary };
